import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { lineupAPI, contactsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useWorkHoursStore } from '../store/workHoursStore';
import { getTimeBasedGreeting, getRandomMotivationalPhrase } from '../utils/greetings';
import StageDistributionChart from '../components/dashboard/StageDistributionChart';
import ActivityFeed from '../components/dashboard/ActivityFeed';

type StatFilter = 'all' | 'contact_today' | 'overdue' | 'in_progress' | null;

// Unified type for lineup contact data displayed in modal
interface LineupContactData {
  contact_id: string;
  contact: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    stage?: string;
    company?: string;
  };
  reason: string;
  best_channel: string;
  best_time: string;
  talking_points: string[];
  opener?: string;
  priority_rank?: number;
  priority_level?: string;
}

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === 'true';
  const { user } = useAuthStore();
  const [activeFilter, setActiveFilter] = useState<StatFilter>(null);
  const [countdown, setCountdown] = useState<string>('');
  const [selectedLineupContact, setSelectedLineupContact] = useState<LineupContactData | null>(null);
  const navigate = useNavigate();
  const { workHours, isWorkingTime } = useWorkHoursStore();
  const queryClient = useQueryClient();
  // Track which lineup ID we've marked as opened to prevent duplicate API calls
  const markedOpenedRef = useRef<string | null>(null);

  // Show countdown to end of work day during configured work hours
  useEffect(() => {
    const updateCountdown = () => {
      // Only show countdown during work hours
      if (!isWorkingTime()) {
        setCountdown('');
        return;
      }

      // Calculate end of work day
      const now = new Date();
      const endOfWorkDay = new Date();
      endOfWorkDay.setHours(workHours.endHour, workHours.endMinute, 0, 0);

      const diff = endOfWorkDay.getTime() - now.getTime();

      if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown(
          `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      } else {
        setCountdown('');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [workHours, isWorkingTime]);

  // Refresh lineup handler
  const handleRefreshLineup = () => {
    queryClient.invalidateQueries({ queryKey: ['lineup', 'today'] });
  };

  // Fetch today's lineup
  const { data: lineupData, isLoading: lineupLoading, error: lineupError } = useQuery({
    queryKey: ['lineup', 'today'],
    queryFn: () => lineupAPI.getToday(),
    refetchOnMount: true,
  });

  // Fetch contact stats
  const { data: statsData } = useQuery({
    queryKey: ['contacts', 'stats'],
    queryFn: () => contactsAPI.getStats(),
    refetchOnMount: true,
  });

  // Fetch contacts for the active filter
  const { data: filteredContacts } = useQuery({
    queryKey: ['contacts', 'filtered', activeFilter],
    queryFn: () => contactsAPI.list({ filter: activeFilter, limit: 20 }),
    enabled: !!activeFilter,
  });

  // Mark lineup as opened (only once per lineup ID)
  useEffect(() => {
    const lineupId = lineupData?.data?.lineup?.id;
    if (lineupId && markedOpenedRef.current !== lineupId) {
      markedOpenedRef.current = lineupId;
      lineupAPI.markOpened().catch(console.error);
    }
  }, [lineupData]);

  const lineup = lineupData?.data?.lineup;
  const stats = statsData?.data;
  const isGenerating = lineupData?.data?.status === 'generating';

  const filterLabels: Record<string, string> = {
    all: 'All Contacts',
    contact_today: 'Contact Today',
    overdue: 'Overdue Contacts',
    in_progress: 'In Progress',
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Message */}
      {isWelcome && (
        <div className="mb-6 bg-ondeck-50 dark:bg-ondeck-900/30 border border-ondeck-200 dark:border-ondeck-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-ondeck-800 dark:text-ondeck-300">Welcome to OnDeck!</h2>
          <p className="text-ondeck-700 dark:text-ondeck-400 mt-1">
            Let's get you set up. Start by adding your contacts or importing them from a CSV.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {getTimeBasedGreeting(user?.name)}
          </h1>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-300 rounded-full text-sm font-medium">
            <span className="text-base">🔥</span>
            {getRandomMotivationalPhrase()}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <p className="text-gray-600 dark:text-gray-400">
            Here's your lineup for today — {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          {countdown && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg text-sm font-mono font-medium">
              <span className="text-base">⏱️</span>
              <span className="tabular-nums">{countdown}</span>
              <span className="text-xs font-sans">to hit today's goals!</span>
            </span>
          )}
        </div>
      </div>

      {/* Stats Cards - Clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <StatCard
          label="Total Contacts"
          value={stats?.total || 0}
          icon="👥"
          onClick={() => setActiveFilter('all')}
          clickable
        />
        <StatCard
          label="Contact Today"
          value={stats?.contact_today || 0}
          icon="📞"
          highlight
          onClick={() => setActiveFilter('contact_today')}
          clickable
        />
        <StatCard
          label="Overdue"
          value={stats?.overdue || 0}
          icon="⏰"
          warning={stats?.overdue > 0}
          onClick={() => setActiveFilter('overdue')}
          clickable
        />
        <StatCard
          label="In Progress"
          value={(stats?.by_stage?.on_base || 0) + (stats?.by_stage?.scoring_position || 0)}
          icon="🔥"
          onClick={() => setActiveFilter('in_progress')}
          clickable
        />
      </div>

      {/* Stage Distribution Chart */}
      {stats?.by_stage && (
        <div className="mb-8">
          <StageDistributionChart byStage={stats.by_stage} />
        </div>
      )}

      {/* Activity Feed */}
      <div className="mb-8">
        <ActivityFeed />
      </div>

      {/* Trading Card Modal */}
      {activeFilter && (
        <TradingCardModal
          title={filterLabels[activeFilter]}
          contacts={filteredContacts?.data?.contacts || []}
          onClose={() => setActiveFilter(null)}
        />
      )}

      {/* Daily Lineup */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Today's Lineup</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">AI-prioritized contacts to reach out to today</p>
            </div>
            <button
              onClick={handleRefreshLineup}
              className="text-sm text-ondeck-600 dark:text-ondeck-400 hover:text-ondeck-700 dark:hover:text-ondeck-300 font-medium"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="p-6">
          {lineupLoading || isGenerating ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ondeck-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">
                {isGenerating ? 'Generating your lineup...' : 'Loading lineup...'}
              </p>
              {isGenerating && (
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">This may take up to 30 seconds</p>
              )}
            </div>
          ) : lineupError ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">Unable to load lineup. Please try again.</p>
            </div>
          ) : lineup ? (
            <div className="space-y-8">
              {/* Daily Pick - Top 3 */}
              {lineup.daily_pick?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <span>🎯</span>
                    <span>Today's Top 3</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {lineup.daily_pick.slice(0, 3).map((pick: any, index: number) => (
                      <DailyPickCard
                        key={pick.contact_id}
                        pick={pick}
                        rank={index + 1}
                        onClick={() => setSelectedLineupContact({
                          contact_id: pick.contact_id,
                          contact: pick.contact || {},
                          reason: pick.reason,
                          best_channel: pick.best_channel,
                          best_time: pick.best_time_window || 'morning',
                          talking_points: pick.engagement_signals || [],
                          opener: undefined,
                        })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Priority Contacts */}
              {lineup.priority_contacts?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <span>📋</span>
                    <span>Priority Lineup</span>
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                      ({lineup.priority_contacts.length})
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {lineup.priority_contacts.map((contact: any) => (
                      <PriorityContactCard
                        key={contact.contact_id}
                        contact={contact}
                        onClick={() => setSelectedLineupContact({
                          contact_id: contact.contact_id,
                          contact: contact.contact || {},
                          reason: contact.reason,
                          best_channel: contact.suggested_action?.type || 'call',
                          best_time: contact.best_time || 'morning',
                          talking_points: contact.talking_points || [],
                          opener: contact.suggested_action?.message,
                          priority_rank: contact.priority_rank,
                          priority_level: contact.priority_level,
                        })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!lineup.daily_pick?.length && !lineup.priority_contacts?.length && (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">⚾</div>
                  <p className="text-gray-600 dark:text-gray-400">No contacts in your lineup today.</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    Add some contacts to get AI-powered recommendations.
                  </p>
                  <Link
                    to="/contacts"
                    className="inline-block mt-4 px-4 py-2 bg-ondeck-600 text-white rounded-lg hover:bg-ondeck-700"
                  >
                    Add Contacts
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">⚾</div>
              <p className="text-gray-600 dark:text-gray-400">No lineup generated yet.</p>
              <button
                onClick={handleRefreshLineup}
                className="mt-4 px-4 py-2 bg-ondeck-600 text-white rounded-lg hover:bg-ondeck-700"
              >
                Generate Lineup
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lineup Contact Modal */}
      {selectedLineupContact && (
        <LineupContactModal
          contact={selectedLineupContact}
          onClose={() => setSelectedLineupContact(null)}
          onNavigate={() => {
            const contactId = selectedLineupContact.contact?.id || selectedLineupContact.contact_id;
            setSelectedLineupContact(null);
            navigate(`/contacts/${contactId}`);
          }}
        />
      )}
    </div>
  );
}

// Helper Components

function StatCard({
  label,
  value,
  icon,
  highlight,
  warning,
  onClick,
  clickable,
}: {
  label: string;
  value: number;
  icon: string;
  highlight?: boolean;
  warning?: boolean;
  onClick?: () => void;
  clickable?: boolean;
}) {
  const baseClasses = `rounded-lg p-4 ${
    highlight
      ? 'bg-ondeck-50 dark:bg-ondeck-900/30 border border-ondeck-200 dark:border-ondeck-800'
      : warning
      ? 'bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800'
      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
  }`;

  const clickableClasses = clickable
    ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all'
    : '';

  return (
    <div className={`${baseClasses} ${clickableClasses}`} onClick={onClick}>
      <div className="flex items-center gap-2">
        <span className="text-xl">{icon}</span>
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
        {clickable && (
          <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
      <div
        className={`text-2xl font-bold mt-1 ${
          warning ? 'text-orange-600 dark:text-orange-400' : highlight ? 'text-ondeck-600 dark:text-ondeck-400' : 'text-gray-900 dark:text-white'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

// Trading Card Modal - Shows contacts in card format
function TradingCardModal({
  title,
  contacts,
  onClose,
}: {
  title: string;
  contacts: any[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cards Grid - Max 6 visible, scroll for more */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 80px)' }}>
          {contacts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map((contact) => (
                <ContactTradingCard key={contact.id} contact={contact} onClose={onClose} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No contacts found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Lineup Contact Modal - Shows full AI coaching context
function LineupContactModal({
  contact,
  onClose,
  onNavigate,
}: {
  contact: LineupContactData;
  onClose: () => void;
  onNavigate: () => void;
}) {
  const contactName = `${contact.contact?.first_name || ''} ${contact.contact?.last_name || ''}`.trim() || 'Unknown';
  const initials = `${contact.contact?.first_name?.[0] || ''}${contact.contact?.last_name?.[0] || ''}`.toUpperCase() || '?';

  const channelIcons: Record<string, string> = {
    call: '📞',
    email: '✉️',
    text: '💬',
  };

  const timeLabels: Record<string, string> = {
    morning: '🌅 Morning (9-12pm)',
    afternoon: '☀️ Afternoon (12-5pm)',
    evening: '🌙 Evening (5-8pm)',
  };

  const stageColors: Record<string, string> = {
    on_deck: 'from-blue-500 to-blue-600',
    at_bat: 'from-yellow-500 to-orange-500',
    on_base: 'from-green-500 to-emerald-600',
    scoring_position: 'from-orange-500 to-red-500',
    hall_of_fame: 'from-purple-500 to-indigo-600',
    bench: 'from-gray-400 to-gray-500',
  };

  const gradientClass = stageColors[contact.contact?.stage || 'on_deck'] || stageColors.on_deck;

  return (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with contact info */}
        <div className={`bg-gradient-to-r ${gradientClass} px-6 py-4`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-xl">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-white truncate">{contactName}</h2>
              {contact.contact?.company && (
                <p className="text-white/80 text-sm truncate">{contact.contact.company}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-1"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* AI Coaching Content */}
        <div className="p-6 space-y-5">
          {/* Why Reach Out Today */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Why Reach Out Today
            </h3>
            <p className="text-gray-900 dark:text-white">{contact.reason}</p>
          </div>

          {/* Talking Points */}
          {contact.talking_points && contact.talking_points.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Talking Points
              </h3>
              <ul className="space-y-2">
                {contact.talking_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-ondeck-600 dark:text-ondeck-400 mt-0.5">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Personalized Opener */}
          {contact.opener && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Opening Line
              </h3>
              <div className="bg-ondeck-50 dark:bg-ondeck-900/30 border border-ondeck-200 dark:border-ondeck-800 rounded-lg p-3">
                <p className="text-gray-800 dark:text-gray-200 italic">"{contact.opener}"</p>
              </div>
            </div>
          )}

          {/* Best Channel & Time */}
          <div className="flex gap-4">
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Best Channel
              </h3>
              <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                <span className="text-lg">{channelIcons[contact.best_channel] || '📞'}</span>
                <span className="capitalize">{contact.best_channel}</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Best Time
              </h3>
              <p className="text-gray-900 dark:text-white">
                {timeLabels[contact.best_time] || contact.best_time}
              </p>
            </div>
          </div>
        </div>

        {/* Footer with action button */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onNavigate}
            className="w-full bg-ondeck-600 hover:bg-ondeck-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <span>Go to Contact</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Individual Trading Card for a Contact
function ContactTradingCard({ contact, onClose }: { contact: any; onClose: () => void }) {
  const stageColors: Record<string, string> = {
    on_deck: 'from-blue-500 to-blue-600',
    at_bat: 'from-yellow-500 to-orange-500',
    on_base: 'from-green-500 to-emerald-600',
    scoring_position: 'from-orange-500 to-red-500',
    hall_of_fame: 'from-purple-500 to-indigo-600',
    bench: 'from-gray-400 to-gray-500',
  };

  const stageLabels: Record<string, string> = {
    on_deck: 'On Deck',
    at_bat: 'At Bat',
    on_base: 'On Base',
    scoring_position: 'Scoring Position',
    hall_of_fame: 'Hall of Fame',
    bench: 'Bench',
  };

  const gradientClass = stageColors[contact.stage] || stageColors.on_deck;
  const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown';
  const initials = `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase() || '?';

  return (
    <Link
      to={`/contacts/${contact.id}`}
      onClick={onClose}
      className="block bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden hover:shadow-lg transition-all hover:scale-[1.02]"
    >
      {/* Card Header with Stage Gradient */}
      <div className={`bg-gradient-to-r ${gradientClass} px-4 py-3`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-lg">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate">{contactName}</h3>
            {contact.company && (
              <p className="text-white/80 text-sm truncate">{contact.company}</p>
            )}
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* Stage Badge */}
        <div className="flex items-center justify-between">
          <span className={`text-xs font-medium px-2 py-1 rounded-full bg-gradient-to-r ${gradientClass} text-white`}>
            {stageLabels[contact.stage] || contact.stage}
          </span>
          {contact.ai_engagement_score && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {Math.round(contact.ai_engagement_score * 100)}% engaged
            </span>
          )}
        </div>

        {/* Contact Details */}
        <div className="space-y-1.5 text-sm">
          {contact.email && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span className="text-xs">✉️</span>
              <span className="truncate">{contact.email}</span>
            </div>
          )}
          {contact.phone && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span className="text-xs">📞</span>
              <span>{contact.phone}</span>
            </div>
          )}
          {(contact.address_city || contact.address_state) && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span className="text-xs">📍</span>
              <span>{[contact.address_city, contact.address_state].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>

        {/* Last Contact & Source */}
        <div className="pt-2 border-t border-gray-100 dark:border-gray-600 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            {contact.last_contact_at
              ? `Last: ${formatRelativeTime(contact.last_contact_at)}`
              : 'No contact yet'}
          </span>
          {contact.source && (
            <span className="truncate max-w-[100px]">{contact.source}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - timestamp;
  const days = Math.floor(diff / 86400);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function DailyPickCard({ pick, rank, onClick }: { pick: any; rank: number; onClick: () => void }) {
  const contact = pick.contact || {};

  // Distinct colors for each rank position
  const rankStyles = {
    1: {
      badge: 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg shadow-amber-200 dark:shadow-amber-900/50',
      card: 'bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 border-amber-300 dark:border-amber-700',
      ring: 'ring-2 ring-amber-400 dark:ring-amber-500',
    },
    2: {
      badge: 'bg-gradient-to-br from-slate-300 to-slate-400 text-slate-800 shadow-md',
      card: 'bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/40 dark:to-slate-700/40 border-slate-300 dark:border-slate-600',
      ring: '',
    },
    3: {
      badge: 'bg-gradient-to-br from-orange-300 to-orange-400 text-orange-900 shadow-md',
      card: 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/20 border-orange-300 dark:border-orange-700',
      ring: '',
    },
  };

  const style = rankStyles[rank as keyof typeof rankStyles] || rankStyles[3];
  const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown';

  return (
    <button
      onClick={onClick}
      className={`block w-full text-left rounded-lg p-3 border ${style.card} ${style.ring} hover:shadow-lg transition-all cursor-pointer`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold flex-shrink-0 ${style.badge}`}>
          {rank}
        </span>
        <span className="font-medium text-gray-900 dark:text-white text-sm truncate">
          {contactName}
        </span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-1">{pick.reason}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">{pick.best_channel}</span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

function PriorityContactCard({ contact, onClick }: { contact: any; onClick: () => void }) {
  const priorityStyles = {
    urgent: {
      border: 'border-red-300 dark:border-red-700',
      bg: 'bg-red-50 dark:bg-red-900/20',
      badge: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
    },
    high: {
      border: 'border-orange-300 dark:border-orange-700',
      bg: 'bg-orange-50 dark:bg-orange-900/20',
      badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
    },
    medium: {
      border: 'border-yellow-300 dark:border-yellow-700',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      badge: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300',
    },
    low: {
      border: 'border-green-300 dark:border-green-700',
      bg: 'bg-green-50 dark:bg-green-900/20',
      badge: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
    },
  };

  const style = priorityStyles[contact.priority_level as keyof typeof priorityStyles] || priorityStyles.medium;
  const contactData = contact.contact || {};
  const contactName = `${contactData.first_name || ''} ${contactData.last_name || ''}`.trim() || 'Unknown';

  return (
    <button
      onClick={onClick}
      className={`block w-full text-left rounded-lg p-3 border ${style.border} ${style.bg} hover:shadow-md transition-all cursor-pointer`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${style.badge}`}>
          #{contact.priority_rank}
        </span>
        <span className="font-medium text-gray-900 dark:text-white text-sm truncate flex-1">
          {contactName}
        </span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">{contact.reason}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {contact.suggested_action?.type || 'Follow up'}
        </span>
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

